import { Injectable, OnDestroy } from '@angular/core';
import { CheckoutSelectors, CustomCheckoutPersistence, StateWithCustomCheckout } from '@checkout/data-access';
import { Store } from '@ngrx/store';
import { StatePersistenceService, StorageSyncType } from '@spartacus/core';
import { Observable, Subscription } from 'rxjs';
import { map, withLatestFrom } from 'rxjs/operators';
import { CheckoutContactFormService } from './checkout-contact-form.service';
import { CustomCheckoutService } from './custom-checkout.service';
import { DeliveryAddressService } from './delivery-address.service';
import { PickupService } from './pickup.service';

@Injectable({ providedIn: 'root' })
export class CheckoutPersistenceService implements OnDestroy {
  protected subscription = new Subscription();

  constructor(
    protected statePersistenceService: StatePersistenceService,
    private customCheckoutService: CustomCheckoutService,
    private pickupService: PickupService,
    private deliveryAddressService: DeliveryAddressService,
    private store: Store<StateWithCustomCheckout>,
    private checkoutContactFormService: CheckoutContactFormService,
  ) {}

  sync(): void {
    this.subscription.add(
      this.statePersistenceService.syncWithStorage({
        key: 'customCheckout',
        state$: this.getCheckoutState(),
        storageType: StorageSyncType.SESSION_STORAGE,
        onRead: (state) => this.onRead(state),
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onRead(state: CustomCheckoutPersistence): void {
    if (!state) {
      return;
    }

    if (state.redirectedToPaymentPage.value) {
      this.customCheckoutService.setRedirectedToPaymentPage(true);
    }

    if (state.orderDetails && Object.keys(state.orderDetails).length > 0) {
      this.customCheckoutService.setOrderDetails(state.orderDetails);
    }

    if (state.activeModes.receiptType) {
      this.customCheckoutService.updateReceiptType(state.activeModes.receiptType);

      // при текущей реализации чекаута необходимо проверять receiptType, чтобы не получилось
      // одновременно и выбранный адрес и магазин
      if (state.activeModes.receiptType === 'pickup' && state.activeModes.pickup) {
        this.pickupService.setShop(state.activeModes.pickup);
      }

      if (state.activeModes.receiptType === 'delivery' && state.activeModes.addressId) {
        this.deliveryAddressService.selectActiveAddressId(state.activeModes.addressId);
      }
    }

    this.checkoutContactFormService.formValues$.next({
      ...this.checkoutContactFormService.formValues$.value,
      ...state.contactForm,
    });
  }

  private getCheckoutState(): Observable<CustomCheckoutPersistence> {
    return this.store.select(CheckoutSelectors.getCustomCheckoutState).pipe(
      withLatestFrom(this.checkoutContactFormService.formValues$),
      map(([state, contactForm]) => ({ ...state, contactForm })),
    );
  }
}
